<!-- 
Dialog for editing a notice template.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg" >
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>
            {{ title }}
            <span class="body-1">{{ subtitle }}</span>
          </v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="levy-collection-setup" section="notice-templates" />
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex grow>
                <v-text-field
                  v-if="show"
                  ref="subject" :value="levyCollectionTemplate.subject" :rules="[rules.required]"
                  label="Subject*" required class="notice-template-subject"
                  @input="update('subject', $event)"
                />
              </v-flex>
              <v-flex shrink class="text-xs-right pt-3">
                <subject-tags
                  :levy-collection-id="levyCollectionId" for-subject
                  field-name="subject" icon="short_text" label="Subject tags"
                  title="Select a notice merge tag to insert into the template subject"
                  @select="insertTag"
                />
                <body-tags
                  :levy-collection-id="levyCollectionId"
                  field-name="body" icon="view_headline" label="Body tags"
                  title="Select a notice merge tag to insert into the template body"
                  @select="insertTag"
                />
              </v-flex>
              <v-flex xs12 sm12>
                <mavon-editor
                  v-show="show && levyCollectionTemplate.body !== null"
                  ref="body"
                  :value="levyCollectionTemplate && levyCollectionTemplate.body ? levyCollectionTemplate.body : ''"
                  :box-shadow="false"
                  :toolbars="toolbars"
                  :rules="[rules.required]"
                  font-size="12px"
                  language="en"
                  style="height: 400px"
                  @change="update('body', $event)"
                />
              </v-flex>
            </v-layout>
          </v-container>
          <our-required-field-indicator/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
          <v-btn
            ref="save-button" :disabled="busy" flat color="success"
            type="submit" @click.prevent="save"
          >Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" />
  </v-dialog>
</template>

<script>
import SubjectTags from "@/components/notice-templates/NoticeTemplateTags.vue";
import BodyTags from "@/components/notice-templates/NoticeTemplateTags.vue";
import filterMixins from "@/mixins/filter.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import mavonMixins from "@/mixins/mavon.mixins";
import * as action from "@/store/notice-templates/action-types";
import * as mutation from "@/store/notice-templates/mutation-types";

export default {
  components: {
    SubjectTags,
    BodyTags
  },
  mixins: [ruleMixins, lookupMixins, filterMixins, mavonMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    levyCollectionId: {
      required: true,
      type: Number
    },
    noticeType: {
      required: true,
      type: String
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showValidationDialog: false
    };
  },
  computed: {
    levyCollection() {
      return this.$store.state.levyCollections.view.data;
    },
    levyCollectionDescription() {
      return `${this.levyCollectionTemplate.levyCollectionYear} ${
        this.lookups.levyCollectionTypeDescriptions[this.levyCollectionTemplate.levyCollectionType]
      }`;
    },
    type() {
      return this.lookups.noticeTypeDescriptions[this.noticeType];
    },
    state() {
      return this.$store.state.noticeTemplates.edit;
    },
    loading() {
      return this.state.loading;
    },
    levyCollectionTemplate() {
      return this.state.data;
    },
    busy() {
      return this.state.loading || this.state.saving;
    },
    title() {
      return !this.state.loading ? `Edit ${this.type}` : "Edit levy collection template";
    },
    subtitle() {
      return !this.state.loading ? this.levyCollectionDescription : "";
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.reset();
        this.$store.dispatch(action.EDIT_GET, { levyCollectionId: this.levyCollectionId, noticeType: this.noticeType });
      }
    },
    loading() {
      if (!this.loading) {
        this.$nextTick(function() {
          var textArea = document.getElementsByClassName("auto-textarea-input")[0];
          textArea.setSelectionRange(0, 0);
          textArea.focus();
        });
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_SET_DATA, { [field]: newValue });
    },
    async save() {
      const data = {
        levyCollectionId: this.levyCollectionId,
        type: this.noticeType
      };
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.EDIT_SAVE, data))) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    insertTag(field, tag) {
      const el = this.$refs[field].$el;
      const f = el.querySelector("input") || el.querySelector("textarea");
      const pos = f.selectionStart;
      this.update(
        field,
        `${f.value.substring(0, f.selectionStart)}{{${tag.name}}}${f.value.substring(f.selectionEnd)}`
      );
      f.focus();
      this.$nextTick(() => {
        f.selectionStart = pos;
        f.selectionEnd = pos + tag.name.length + 4;
      });
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>

<style lang="scss">
.notice-template-body {
  font-size: 12px;
  font-family: monospace;
}
</style>